import React, { useState, useRef, useEffect } from "react";
import { EditIcon } from "../Icons/Interface";

interface EditableLabelProps {
	value: string;
	onSubmit: (newValue: string) => void;
	className?: string;
	inputClassName?: string;
}

const EditableLabel: React.FC<EditableLabelProps> = ({
	value,
	onSubmit,
	className = "",
	inputClassName = ""
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(value);
	const inputRef = useRef<HTMLInputElement>(null);

	// Update local state when prop changes
	useEffect(() => {
		setEditValue(value);
	}, [value]);

	// Focus input when entering edit mode
	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditing]);

	const handleEdit = () => {
		setIsEditing(true);
	};

	const handleSubmit = () => {
		if (editValue.trim() && editValue !== value) {
			onSubmit(editValue.trim());
		}
		setIsEditing(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			handleSubmit();
		} else if (e.key === "Escape") {
			e.preventDefault();
			e.stopPropagation();
			setEditValue(value);
			setIsEditing(false);
		}
	};

	const handleBlur = () => {
		handleSubmit();
	};

	if (isEditing) {
		return (
			<div className={`flex items-center gap-2 ${className}`}>
				<input
					ref={inputRef}
					type="text"
					value={editValue}
					onChange={(e) => setEditValue(e.target.value)}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					size={Math.max(editValue.length, 1)}
					className={`bg-primary-background border border-primary-active rounded px-2 py-1 text-primary focus:outline-none focus:ring-2 focus:ring-primary-active ${inputClassName}`}
				/>
			</div>
		);
	}

	return (
		<div className={`flex items-center gap-2 group ${className}`}>
			<span
				onClick={handleEdit}
				className={`cursor-pointer px-2 py-1 border border-transparent rounded ${inputClassName}`}
			>
				{editValue}
			</span>
			<button
				onClick={handleEdit}
				className="opacity-0 group-hover:opacity-100 transition-opacity text-primary-inactive hover:text-primary-active"
				aria-label="Edit"
			>
				<div className="w-5 h-5">{EditIcon()}</div>
			</button>
		</div>
	);
};

export default EditableLabel;
